import { DateTime } from "luxon"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack, Typography } from "@mui/material"
import { SelectedPriceListItem, CreateOrderDialogMode, CreateOrderFlowType } from "../../../../../createOrderTypes"
import { useCreateOrderFormConfig, useCreateOrderFormState } from "../../../../../mainForm/CreateOrderFormContext"
import { useCreateOrderDialogType } from "../../../../../hooks/useCreateOrderDialogType"
import { DeadlineMessageQuery, DeadlineMessageResponse, fetchDeadlineMessage } from "../../deliveryApi"
import { LocalizedStrict } from "../../../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../../../modules/localization/useDateTime"
import { CreateUpdateDeliveryFormValues } from "../../CreateUpdateDeliveryTimePopover"
import { FormikDatePickerControlled } from "../../../../../../forms/Formik"
import { useApiQuery } from "../../../../../../../../core/api/useApiQuery"
import { validateDeliveryDate } from "../../helpers"
import { CustomDay } from "./CustomDay"

const useStyles = makeStyles()((theme) => ({
    message: {
        borderRadius: ".5rem",
        padding: ".75rem 1rem",
        fontWeight: "500",
    },
    messageInfo: {
        color: theme.palette.tailwind.blue[950],
        background: theme.palette.tailwind.sky[100],
    },
    subtitle: {
        color: theme.palette.tailwind.slate[700],
    },
    subtitleDetails: {
        display: "inline-block",
    },
}))

const transformData = (data: DeadlineMessageResponse) => {
    return data.deadlineMessage
}

type DeliveryDatePickerProps = Readonly<{
    closingDates: string[]
    priceList: SelectedPriceListItem
    allowedWeekdays: number[]
    canOverrideClosingDates: boolean
    grandfatheredDate?: DateTime | null
    eventStartTime: DateTime | null
    isDeliveryBlocked: boolean
    allowPastDates: boolean
}>

export function DeliveryDatePicker({
    closingDates,
    priceList,
    allowedWeekdays,
    canOverrideClosingDates,
    grandfatheredDate,
    eventStartTime,
    isDeliveryBlocked,
    allowPastDates,
}: DeliveryDatePickerProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { flowConfig } = useCreateOrderFormConfig()
    const { orderContext } = useCreateOrderFormState()
    const { isRegularOrder } = useCreateOrderDialogType()
    const canEditDeliveryDate = isRegularOrder && orderContext.mode === CreateOrderDialogMode.EDIT

    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")

    const selectedTimes = deliveryDate && dateTimeFactory.toApi(deliveryDate)
    const dateRules = { allowedWeekdays, closingDates, canOverrideClosingDates, grandfatheredDate }
    const { isSelectable } = validateDeliveryDate(deliveryDate, dateRules, dateTimeFactory)

    const { data } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: {
            merchantId: priceList.merchantId,
            deliveryTime: selectedTimes!,
            priceListId: priceList.priceListId,
            shopId: priceList.shopId,
        },
        select: transformData,
        enabled: !!selectedTimes && !isDeliveryBlocked && isSelectable,
    })

    // A booked resource no longer locks the delivery date: catering may be delivered on a
    // different day than the meeting. Only the Outlook flow keeps its date, which comes from
    // the calendar event itself — and only while the meeting's own day can be delivered on, or
    // the user would be left with a date they can neither use nor change. Keyed on the meeting,
    // not on the picked value, so choosing a date does not lock the picker away mid-edit.
    const isEventDaySelectable = validateDeliveryDate(eventStartTime, dateRules, dateTimeFactory).isSelectable
    const isDateLockedToEvent =
        !canEditDeliveryDate && flowConfig?.flow === CreateOrderFlowType.OUTLOOK && isEventDaySelectable
    const isDisabledDatePicker = isDateLockedToEvent && !!deliveryDate

    return (
        <Stack gap=".75rem">
            {isDisabledDatePicker ? (
                <Typography variant="body1" className={classes.subtitle}>
                    <LocalizedStrict id="create-order-delivery-times-dialog-delivery-date-details">
                        <Typography className={classes.subtitleDetails}>Delivery date:</Typography>
                    </LocalizedStrict>{" "}
                    <Typography className={classes.subtitleDetails} fontWeight={700}>
                        {deliveryDate ? dateTimeFactory.formatDate(deliveryDate) : ""}
                    </Typography>
                </Typography>
            ) : (
                <LocalizedStrict id="create-order-delivery-times-dialog-delivery-date">
                    <Typography variant="body1" className={classes.subtitle}>
                        Delivery date
                    </Typography>
                </LocalizedStrict>
            )}
            <Stack gap=".75rem">
                {!isDisabledDatePicker && (
                    <FormikDatePickerControlled
                        name="deliveryDate"
                        range={allowPastDates ? "all" : "now-to-future"}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                        slots={{
                            day: (props) => (
                                <CustomDay
                                    {...props}
                                    closingDates={closingDates}
                                    allowedWeekdays={allowedWeekdays}
                                    canOverrideClosingDates={canOverrideClosingDates}
                                    grandfatheredDate={grandfatheredDate}
                                    allowPastDates={allowPastDates}
                                />
                            ),
                        }}
                        disablePast={!allowPastDates}
                        disabled={isDateLockedToEvent}
                    />
                )}
                {data?.message?.displayRule && isSelectable && (
                    <Typography variant="body2" className={cx(classes.message, classes.messageInfo)}>
                        {data.message.displayRule}
                    </Typography>
                )}
            </Stack>
        </Stack>
    )
}
